import { Skeleton } from "antd";
import { Anchor } from "client/src/components/Anchor/Anchor";
import { useGetClientByID } from "client/src/hooks/client";

import { useGetActiveRouteData } from "client/src/hooks/route";
import { useTrackElementClicked } from "client/src/utils/analytics";
import { getImplementationContactInfo } from "client/src/utils/getImplementationContactInfo";
import clsx from "clsx";
import { useCallback } from "react";
import { SHARED_IC_LABEL, isClientUsingSharedIC } from "shared/utils/client";

import * as styles from "./supportFooter.module.css";

import type { QueryStatus } from "@tanstack/react-query";

export function SupportFooter() {
  const activeRouteData = useGetActiveRouteData();
  const clientId = activeRouteData?.params.clientId ?? "";
  const isAuthRequiredPage = activeRouteData?.authType === "required";
  const { status, data: client } = useGetClientByID(clientId, {
    enabled: isAuthRequiredPage,
  });
  const trackElementClicked = useTrackElementClicked(client);
  const track = useCallback(
    (buttonLabel: string) => {
      trackElementClicked({
        module: "Support Footer",
        buttonLabel,
      });
    },
    [trackElementClicked],
  );
  const {
    implementationConsultantFullName,
    implementationConsultantPhone,
    implementationConsultantEmail,
  } = getImplementationContactInfo(client);

  const isSharedIC = isClientUsingSharedIC(client);

  const isOnboardingComplete = Boolean(client?.releaseStatus === "LIVE");

  if (!isAuthRequiredPage) {
    return null;
  }

  return (
    <SupportFooterPresentation
      status={status}
      isOnboardingComplete={isOnboardingComplete}
      implementationConsultantFullName={implementationConsultantFullName}
      implementationConsultantPhone={implementationConsultantPhone}
      implementationConsultantEmail={implementationConsultantEmail}
      isSharedIC={isSharedIC}
      track={track}
    />
  );
}

type SupportFooterPresentationProps = {
  status: QueryStatus;
  isOnboardingComplete: boolean | undefined;
  implementationConsultantFullName: string | undefined;
  implementationConsultantPhone: string | undefined;
  implementationConsultantEmail: string | undefined;
  isSharedIC?: boolean;
  track: (buttonLabel: string) => void;
};

export function SupportFooterPresentation(props: SupportFooterPresentationProps) {
  const {
    status,
    isOnboardingComplete,
    implementationConsultantFullName,
    implementationConsultantPhone,
    implementationConsultantEmail,
    isSharedIC = false,
    track,
  } = props;

  return (
    <div className={styles.supportFooterContainer}>
      <div className={clsx(styles.content, "py-[48px] px-[32px]")}>
        <p className="h2">Need Help?</p>

        <div className={styles.columns}>
          <div>
            {isOnboardingComplete ? (
              <OnboardHelpCenter track={track} />
            ) : (
              <>
                {status === "pending" && (
                  <>
                    <Skeleton active paragraph={false} title={{ width: 140 }} />

                    <Skeleton active paragraph={false} title={{ width: 500 }} />
                    <Skeleton active paragraph={false} title={{ width: 170 }} />
                  </>
                )}

                {status === "error" && <OnboardHelpCenter track={track} />}

                {status === "success" && (
                  <>
                    {isSharedIC ? (
                      <p className="body2">{SHARED_IC_LABEL}</p>
                    ) : (
                      <>
                        {implementationConsultantFullName && (
                          <p className="body2">{implementationConsultantFullName}</p>
                        )}

                        <p>
                          Your personal Implementation Consultant and source for answers about
                          plans, pricing, forms and enrollments.
                        </p>
                      </>
                    )}

                    {(implementationConsultantPhone || implementationConsultantEmail) && (
                      <address className="body2">
                        {!isSharedIC && implementationConsultantPhone && (
                          <p>
                            <Anchor
                              variant="bold"
                              href={`tel:${implementationConsultantPhone}`}
                              onClick={() => track("implementation consultant phone")}
                            >
                              {implementationConsultantPhone}
                            </Anchor>
                          </p>
                        )}
                        {implementationConsultantEmail && (
                          <Anchor
                            variant="bold"
                            href={`mailto:${implementationConsultantEmail}`}
                            onClick={() => track("implementation consultant email")}
                            target="_blank"
                          >
                            {implementationConsultantEmail}
                          </Anchor>
                        )}
                      </address>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {!isOnboardingComplete && status !== "error" && (
            <div>
              <OnboardHelpCenter track={track} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OnboardHelpCenter({ track }: { track: (buttonLabel: string) => void }) {
  return (
    <div>
      <p className="body2">Onboard Help Center</p>

      <p>Get the answers you need to the most common support questions.</p>

      <Anchor
        variant="bold"
        href="https://onboard-help.sunlifeconnect.com/hc/en-us"
        target="_blank"
        onClick={() => track("Support Library")}
      >
        Visit the Help Center
      </Anchor>
    </div>
  );
}
